import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  getPermissionsByService,
} from '../api/permissions';

/**
 * PermissionsContext - единственный контекст для работы с permissions в приложении
 * 
 * Этот контекст получает permissions из AuthContext (user.permissions) вместо отдельного API запроса.
 * Permissions загружаются вместе с информацией о пользователе через /api/auth/me endpoint.
 * 
 * Этот контекст предоставляет все методы для проверки permissions пользователя:
 * - hasPermission(service, resource, action) - основная функция проверки
 * - hasServiceAccess(service) - проверка доступа к сервису
 * - hasResourceAccess(service, resource) - проверка доступа к ресурсу
 * - hasAllPermissions(permissions) - проверка всех permissions
 * - hasAnyPermission(permissions) - проверка хотя бы одного permission
 * 
 * ВНИМАНИЕ: Не используйте AuthContext.hasPermission() - он устарел и всегда возвращает true
 */

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsByService, setPermissionsByService] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [permissionsCache, setPermissionsCache] = useState(new Map());
  
  // Load user permissions from user object (no separate API call needed)
  const loadUserPermissions = useCallback(() => {
    if (!user?.id) {
      setUserPermissions([]);
      setPermissionsCache(new Map());
      setIsLoading(false);
      return;
    }

    // Get permissions from user object (assuming user.permissions exists)
    const permissions = user.permissions || [];

    if (Array.isArray(permissions) && permissions.length > 0) {
      const cache = new Map();

      permissions.forEach(permission => {
        const exactKey = `${permission.service}.${permission.resource}.${permission.action}`;
        cache.set(exactKey, permission);

        if (permission.service === '*' && permission.resource === '*' && permission.action === '*') {
          cache.set('*.*.*', permission);
        } else if (permission.service !== '*' && permission.resource === '*' && permission.action === '*') {
          cache.set(`${permission.service}.*.*`, permission);
        } else if (permission.service !== '*' && permission.resource !== '*' && permission.action === '*') {
          cache.set(`${permission.service}.${permission.resource}.*`, permission);
        }
      });

      setUserPermissions(permissions);
      setPermissionsCache(cache);
    } else {
      setUserPermissions([]);
      setPermissionsCache(new Map());
    }
    setIsLoading(false);
  }, [user?.id, user?.permissions]);
  
  // Load permissions grouped by service
  const loadPermissionsByService = useCallback(async () => {
    try {
      const permissions = await getPermissionsByService();
      setPermissionsByService(permissions);
    } catch (error) {
      console.error('Error loading permissions by service:', error);
    }
  }, []);
  
  // Check if user has specific permission
  const hasPermission = useCallback((service, resource, action) => {
    if (!user || !userPermissions.length) {
      return false;
    }
    
    // Check cache first for exact match
    const key = `${service}.${resource}.${action}`;
    if (permissionsCache.has(key)) {
      return true;
    }
    
    // Check for wildcard permissions
    // 1. Check for global wildcard: *.*.*
    const globalWildcard = userPermissions.some(permission => 
      permission.service === '*' && 
      permission.resource === '*' && 
      permission.action === '*'
    );
    if (globalWildcard) {
      return true;
    }
    
    // 2. Check for service wildcard: service.*.*
    const serviceWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === '*' && 
      permission.action === '*'
    );
    if (serviceWildcard) {
      return true;
    }
    
    // 3. Check for resource wildcard: service.resource.*
    const resourceWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === resource && 
      permission.action === '*'
    );
    if (resourceWildcard) {
      return true;
    }
    
    // 4. Check for action wildcard: service.*.action
    const actionWildcard = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === '*' && 
      permission.action === action
    );
    if (actionWildcard) {
      return true;
    }
    
    // 5. Check for exact match
    const hasExactPerm = userPermissions.some(permission => 
      permission.service === service && 
      permission.resource === resource && 
      permission.action === action
    );
    
    return hasExactPerm;
  }, [user, userPermissions, permissionsCache]);
  
  // Check if user has any permission for a service
  const hasServiceAccess = useCallback((service) => {
    if (!user || !userPermissions.length) return false;
    
    // Check for global wildcard
    const hasGlobalWildcard = userPermissions.some(permission => 
      permission.service === '*' && permission.resource === '*' && permission.action === '*'
    );
    if (hasGlobalWildcard) return true;
    
    // Check for service wildcard
    const hasServiceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === '*' && permission.action === '*'
    );
    if (hasServiceWildcard) return true;
    
    // Check for any permission in the service
    return userPermissions.some(permission => permission.service === service);
  }, [user, userPermissions]);
  
  // Check if user has any permission for a resource within a service
  const hasResourceAccess = useCallback((service, resource) => {
    if (!user || !userPermissions.length) return false;
    
    // Check for global wildcard
    const hasGlobalWildcard = userPermissions.some(permission => 
      permission.service === '*' && permission.resource === '*' && permission.action === '*'
    );
    if (hasGlobalWildcard) return true;
    
    // Check for service wildcard
    const hasServiceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === '*' && permission.action === '*'
    );
    if (hasServiceWildcard) return true;
    
    // Check for resource wildcard
    const hasResourceWildcard = userPermissions.some(permission => 
      permission.service === service && permission.resource === resource && permission.action === '*'
    );
    if (hasResourceWildcard) return true;
    
    // Check for any permission in the resource
    return userPermissions.some(permission => 
      permission.service === service && permission.resource === resource
    );
  }, [user, userPermissions]);
  
  // Check if user has specific action permission
  const hasActionPermission = useCallback((service, resource, action) => {
    return hasPermission(service, resource, action);
  }, [hasPermission]);
  
  // Check multiple permissions (all must be true)
  const hasAllPermissions = useCallback((permissions) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.every(([service, resource, action]) => 
      hasPermission(service, resource, action)
    );
  }, [hasPermission]);
  
  // Check multiple permissions (any can be true)
  const hasAnyPermission = useCallback((permissions) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(([service, resource, action]) => 
      hasPermission(service, resource, action)
    );
  }, [hasPermission]);
  
  // Get user permissions for a specific service
  const getServicePermissions = useCallback((service) => {
    return userPermissions.filter(permission => permission.service === service);
  }, [userPermissions]);
  
  // Get user permissions for a specific resource
  const getResourcePermissions = useCallback((service, resource) => {
    return userPermissions.filter(permission => 
      permission.service === service && permission.resource === resource
    );
  }, [userPermissions]);
  
  // Refresh user permissions
  const refreshPermissions = useCallback(() => {
    loadUserPermissions();
  }, [loadUserPermissions]);
  
  // Check permission with server validation (now just uses local check since permissions come with user)
  const checkPermissionWithServer = useCallback((service, resource, action) => {
    // Since permissions are now loaded with user data, we can just use local check
    return hasPermission(service, resource, action);
  }, [hasPermission]);
  
  // Load user permissions when user changes
  useEffect(() => {
    if (isAuthLoading) return; // wait for auth to finish first

    if (isAuthenticated && user?.id) {
      loadUserPermissions();
    } else {
      setUserPermissions([]);
      setPermissionsCache(new Map());
      setIsLoading(false);
    }
  }, [isAuthLoading, isAuthenticated, user?.id, user?.permissions, loadUserPermissions]);
  
  const value = useMemo(() => ({
    // State
    userPermissions,
    permissionsByService,
    isLoading,
    
    // Permission checking methods
    hasPermission,
    hasServiceAccess,
    hasResourceAccess,
    hasActionPermission,
    hasAllPermissions,
    hasAnyPermission,
    
    // Utility methods
    getServicePermissions,
    getResourcePermissions,
    refreshPermissions,
    checkPermissionWithServer,
    
    // Loading methods
    loadPermissionsByService,
  }), [
    userPermissions,
    permissionsByService,
    isLoading,
    hasPermission,
    hasServiceAccess,
    hasResourceAccess,
    hasActionPermission,
    hasAllPermissions,
    hasAnyPermission,
    getServicePermissions,
    getResourcePermissions,
    refreshPermissions,
    checkPermissionWithServer,
    loadPermissionsByService,
  ]);
  

  
  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

